import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Modern top-of-viewport animated navigation progress bar.
 * Gives immediate visual feedback as soon as the user clicks any link or route changes,
 * preventing user confusion when lazy-loaded chunks or API calls are resolving.
 */
export const NavigationProgressBar: React.FC = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const finishTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Route has started changing
    setVisible(true);
    setProgress(15);

    if (timerRef.current) clearInterval(timerRef.current);
    if (finishTimerRef.current) clearTimeout(finishTimerRef.current);

    // Incrementally simulate progress while page/chunks load
    timerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 85) {
          if (timerRef.current) clearInterval(timerRef.current);
          return prev;
        }
        const step = Math.max(2, (90 - prev) * 0.15);
        return Math.min(88, prev + step);
      });
    }, 120);

    // Complete the bar shortly after location resolves
    finishTimerRef.current = setTimeout(() => {
      if (timerRef.current) clearInterval(timerRef.current);
      setProgress(100);
      const hideTimer = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 300);
      return () => clearTimeout(hideTimer);
    }, 200);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (finishTimerRef.current) clearTimeout(finishTimerRef.current);
    };
  }, [location.pathname, location.search]);

  if (!visible && progress === 0) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[99999] pointer-events-none transition-opacity duration-300"
      style={{ opacity: visible ? 1 : 0 }}
      aria-hidden="true"
    >
      {/* 3px Glowing Progress Line */}
      <div
        className="h-[3px] bg-gradient-to-r from-purple-600 via-pink-500 to-indigo-500 transition-all duration-200 ease-out shadow-[0_0_12px_rgba(168,85,247,0.7)]"
        style={{
          width: `${progress}%`,
        }}
      />
      {/* Glowing Leading Head */}
      <div
        className="absolute top-0 h-[3px] w-24 bg-white/80 blur-[2px] transition-all duration-200 ease-out"
        style={{
          left: `calc(${progress}% - 96px)`,
          opacity: progress < 100 ? 1 : 0,
        }}
      />
    </div>
  );
};
